import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { assetsApi } from "@platformx/authoring-apis";
import { ArrowDownBlack, FolderBlackLarge } from "@platformx/shared/static-assets";
import {
  AUTH_INFO,
  CommonPlateformXDialog,
  NoSearchResult,
  ShowToastSuccess,
} from "@platformx/utilities";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ASSETS, AUTHOR, SUBCOMMUNITIES } from "../../utils/constants";
import AssetFilter from "./AssetFilter";
import { AuthorMenu } from "./AuthorMenu";
import useContentGlleryStyle from "./contentTypeCard/DamContentGllery.style";
import useDamContent from "./DamContent.style";
import DamContentCard from "./DamContentCard";
import DamContentLeftSidebar from "./DamContentLeftSidebar";
import DamContentTopHeading from "./DamContentTopHeading";
import "./DamGallery.css";
import { FilterByMenu } from "./FilterByMenu";
import { FilterDropdown } from "./FilterDropdown";
import LoaderComponent from "./LoaderComponent";
import { SortByMenu } from "./SortByMenu";
import TagMenu from "./TagMenu";

type DamContentGalleryAssetType = "Image" | "Video" | "image" | "video";

type DamContentGalleryProps = {
  assetType: DamContentGalleryAssetType;
  handleImageSelected?: any;
  handleSelectedVideo?: any;
  toggleGallery: any;
  keyName?: string;
  id?: any;
  processing?: boolean;
  dialogOpen?: any;
  isCrop?: boolean;
};

const normalizeAssetType = (assetType: DamContentGalleryAssetType): "Image" | "Video" =>
  assetType.toLowerCase() === "video" ? "Video" : "Image";

// Initial state for collection items and pagination
const COLLECTION_ITEM = {
  collectionItem: [],
  page: {},
};

export const DamContentGallery = (_props: DamContentGalleryProps) => {
  const {
    dialogOpen,
    assetType: assetTypeProp,
    handleImageSelected,
    handleSelectedVideo,
    toggleGallery,
    keyName,
    processing,
  } = _props;
  const assetType = normalizeAssetType(assetTypeProp);
  const { t } = useTranslation();
  const { fetchFacet, fetchDAMContent } = assetsApi;
  const [data, setData] = useState(COLLECTION_ITEM);
  const [menuData, setMenuData] = useState(COLLECTION_ITEM);
  const [loading, setLoading] = useState(true);
  const [startIndex, setStartIndex] = useState<number>(0);
  const [menuStartIndex, setMenuStartIndex] = useState<number>(0);
  // Whether more top-level folders remain to be auto-loaded on scroll.
  const [folderHasMore, setFolderHasMore] = useState<boolean>(false);
  const [filterVal, setFilter] = useState(assetType === "Video" ? "playable" : "default");
  const [sortBy, setSortBy] = useState<"ASC" | "DESC">("DESC");
  const [playbackFilter, setPlaybackFilter] = useState<string>(
    assetType === "Video" ? "PLAYABLE" : "ALL",
  );
  const playbackStatusFilter = assetType === "Video" ? playbackFilter : "ALL";
  const ROWS = 20;
  const FOLDER_ROWS = 16;
  // const [isLazyLoad, setIsLazyLoad] = useState<boolean>(true);
  const [anchorE2, setAnchorE2] = useState<null | HTMLElement>(null);
  const [label, setLabel] = useState<{ author: { label: string }[]; tag: { label: string }[] }>({
    author: [],
    tag: [],
  }); // Ensure correct initialization
  const classes = useDamContent();
  const classesGallery = useContentGlleryStyle();
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  // Read the site's DAM community on every render (AUTH_INFO exposes these as
  // getters over localStorage) so a site switch is picked up. `uuid` stays state
  // because folder navigation writes it, but it must be re-seeded when the root
  // community changes -- a useState initializer alone would freeze the previous
  // site's community into an already-mounted gallery.
  const rootUuid = assetType === "Image" ? AUTH_INFO.dspaceImagesUuid : AUTH_INFO.dspaceVideosUuid;
  const [uuid, setUuid] = useState<any>(rootUuid);
  const [author, setAuthor] = useState("");
  const [tag, setTag] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<any>("");
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [searchItem, setSearchTerm] = useState<any>("");
  const [scheduleNewDateVal, setScheduleNewDateVal] = useState(null);
  const [scheduleEndDateVal, setScheduleEndDateVal] = useState(null);
  const [folder, setFolder] = useState<any>({});
  const [prevFolderState, setPrevFolderState] = useState([]);
  const [totalImageItem, setTotalImageItem] = useState(0);
  const [imageData, setImageData] = useState({
    Thumbnail: "",
    Title: "",
    Description: "",
    Author: "",
    bitStreamId: "",
    Url: "",
    bundlesUrl: "",
    original_image_relative_path: "",
    playbackStatus: "NOT_PLAYABLE" as string,
    videoId: "",
    duration: "",
  });
  const [open, setOpen] = useState(false);
  const [showNonPlayableDialog, setShowNonPlayableDialog] = useState(false);
  const [syncingVideo, setSyncingVideo] = useState(false);
  const navigate = useNavigate();

  // Function to toggle the drawer
  const toggleDrawer = () => {
    setOpen(!open);
  };

  const menuOpenStates = {
    folder: filterVal === "folder",
    date: filterVal === "date",
    tag: filterVal === "tag",
    author: filterVal === "author",
  };

  const {
    folder: folderMenuOpen,
    date: dateMenuOpen,
    tag: tagMenuOpen,
    author: authorMenuOpen,
  } = menuOpenStates;

  const removetag = () => {
    setAuthor("");
    setTag([]);
  };
  // Function to get data from DAM based on index and search term
  const getFacet = async () => {
    // No DAM community to scope the facets to: fail closed, same as the asset
    // and folder queries below.
    if (!uuid) {
      setLabel({ author: [], tag: [] });
      return;
    }
    try {
      const { authorList, tagList } = await fetchFacet({
        scope_id: uuid,
        facet_name: AUTHOR,
      });
      setLabel({ author: authorList, tag: tagList });
    } catch (error) {
      //console.log(error);
    }
  };

  // Fetch DAM content based on index and filter
  const getDAMData = async (
    index,
    filter,
    searchTerm = "",
    playbackStatus = playbackStatusFilter,
  ) => {
    // No DAM community mapped for this site: show the empty grid rather than
    // querying an empty community. getDAMcontent has already flipped `loading`
    // on, so release it here rather than leaving the flag stuck.
    if (!uuid) {
      setData({ collectionItem: [], page: {} });
      setTotalImageItem(0);
      setLoading(false);
      return;
    }
    try {
      const tags: any = {};
      if (filter.author) tags.author = filter.author;
      if (filter.tag?.length > 0) tags.label = filter.tag;
      const { authoring_getAssets = {} } = await fetchDAMContent({
        uuid: uuid,
        start: index,
        rows: ROWS,
        search: searchTerm,
        entityType: assetType,
        assetType: ASSETS,
        tags: tags,
        sortBy: sortBy,
        playbackStatus,
      });
      setTotalImageItem(authoring_getAssets?.page?.totalElements || 0);
      if (authoring_getAssets?.collectionItem?.length > 0) {
        setData({
          collectionItem: authoring_getAssets.collectionItem || [],
          page: authoring_getAssets.page || {},
        });
        // if (authoring_getAssets?.collectionItem?.length < ROWS) {
        //   // setIsLazyLoad(false);
        // } else {
        //   // setIsLazyLoad(true);
        // }
      } else {
        setData({
          collectionItem: [],
          page: {},
        });
        // setIsLazyLoad(false);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      // setIsLazyLoad(false);
    }
  };
  // Fetch additional DAM content for infinite scrolling
  const fetchMoreDAMData = async (
    index,
    searchTerm = "",
    playbackStatus = playbackStatusFilter,
  ) => {
    // Nothing to page through when the site has no DAM community.
    if (!uuid) {
      setLoading(false);
      return;
    }
    try {
      const { authoring_getAssets = {} } = await fetchDAMContent({
        uuid: uuid,
        start: index,
        rows: ROWS,
        search: searchTerm,
        entityType: assetType,
        assetType: ASSETS,
        tags: { author: author },
        sortBy: sortBy,
        playbackStatus,
      });
      let newCollectionItem: any = [];
      // if (authoring_getAssets?.collectionItem?.length < ROWS) {
      //   // setIsLazyLoad(false);
      // }
      if (
        authoring_getAssets.collectionItem &&
        typeof authoring_getAssets.collectionItem[Symbol.iterator] === "function"
      ) {
        newCollectionItem = [...data.collectionItem, ...authoring_getAssets.collectionItem];
      } else if (authoring_getAssets.collectionItem) {
        newCollectionItem = [...data.collectionItem, authoring_getAssets.collectionItem];
      } else {
        newCollectionItem = [...data.collectionItem];
        // setIsLazyLoad(false);
      }
      setData({
        ...data,
        collectionItem: newCollectionItem,
        page: { ...data.page, ...authoring_getAssets.page },
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      // setIsLazyLoad(false);
    }
  };
  // Function to get DAM content based on index and search term

  const getDAMcontent = (index, filter, searchTerm = "", playbackStatus = playbackStatusFilter) => {
    setLoading(true);
    getDAMData(index, filter, searchTerm, playbackStatus);
  };

  // Function to fetch more DAM content

  const fetchMoreDAMcontent = (index, searchTerm = "", playbackStatus = playbackStatusFilter) => {
    // setIsLazyLoad(true);
    fetchMoreDAMData(index, searchTerm, playbackStatus);
  };

  const handlePlaybackFilterChange = (value: string) => {
    setPlaybackFilter(value);
    setStartIndex(0);
    getDAMcontent(0, { author, tag }, searchItem, value);
  };

  // Function to get DAM menu content based on search term

  const getDamMenucontent = async (searchTerm = "", index = 0, append = false) => {
    // No DAM community mapped for this site: fail closed rather than querying an
    // empty community.
    if (!rootUuid) {
      setMenuData(COLLECTION_ITEM);
      setCategoryLoading(false);
      return;
    }
    // Only show the whole-list skeleton on a fresh load. On the append (scroll)
    // path the InfiniteScroll's own loader covers it — toggling categoryLoading
    // here would unmount the list, reset the scroll, and bounce the user to the top.
    if (!append) setCategoryLoading(true);
    try {
      const { authoring_getAssets = {} } = await fetchDAMContent({
        uuid: rootUuid,
        start: index,
        rows: FOLDER_ROWS,
        search: searchTerm,
        entityType: "ALL",
        assetType: SUBCOMMUNITIES,
        tags: {},
        sortBy: "DESC",
        playbackStatus: "ALL",
      });

      const newList = authoring_getAssets.subcommunitiesList || [];
      const page = (authoring_getAssets.page as any) || {};
      // Append when auto-paginating on scroll; replace on a fresh load.
      setMenuData((prev: any) => ({
        collectionItem: append ? [...(prev?.collectionItem || []), ...newList] : newList,
        page,
      }));
      // 0-based page number vs total pages tells us if more folders remain.
      const pageNumber = page?.number ?? index;
      const totalPages = page?.totalPages ?? 1;
      setFolderHasMore(pageNumber < totalPages - 1);

      if (!append) setCategoryLoading(false);
    } catch (error) {
      if (!append) setCategoryLoading(false);
    }
  };

  // Auto-pagination: load the next page of top-level folders and append it.
  const fetchMoreFolders = () => {
    const nextIndex = menuStartIndex + 1;
    setMenuStartIndex(nextIndex);
    getDamMenucontent("", nextIndex, true);
  };
  /**
   * pagination infinity scroll
   */

  const fetchMoreData = () => {
    const nextIndex = startIndex + 1;
    setStartIndex(() => nextIndex);
    data?.collectionItem?.length <= totalImageItem &&
      fetchMoreDAMcontent(nextIndex, searchItem, playbackStatusFilter);
  };
  const handleSearch = (searchTerm) => {
    setSearchTerm(searchTerm);
    const filter = {
      author,
      tag,
    };
    getDAMcontent(0, filter, searchTerm, playbackStatusFilter);
  };

  const handleSyncVideoToPlayer = async () => {
    if (!imageData?.bitStreamId) return;
    setSyncingVideo(true);
    try {
      await assetsApi.syncVideoToPlayer([imageData.bitStreamId]);
      ShowToastSuccess(t("video_sync_initiated"));
      setShowNonPlayableDialog(false);
    } catch (error) {
      // error handled silently
    } finally {
      setSyncingVideo(false);
    }
  };

  const handleDoneClick = () => {
    if (assetType === "Image") {
      handleImageSelected(imageData, keyName);
      // if (!isCrop) toggleGallery(false, "done", keyName);
    } else {
      // Check video playback status — block and show popup if not playable
      const isPlayable = imageData?.playbackStatus === "PLAYABLE";
      if (!isPlayable) {
        setShowNonPlayableDialog(true);
        return;
      }
      try {
        handleSelectedVideo({ ...imageData, Url: imageData.bundlesUrl, uuid });
      } catch (error) {
        //console.log(error);
      }
      toggleGallery(false, "done", keyName);
    }
  };

  // Build the validation popup content based on the video's actual playback status,
  // so Processing/Failed videos no longer show the generic "publish it first" prompt.
  const getPlaybackDialogConfig = () => {
    const status = imageData?.playbackStatus;
    const goToAssetManager = () => {
      setShowNonPlayableDialog(false);
      navigate("/assetmanager/video");
      setTimeout(() => toggleGallery(false, "cancel", keyName), 0);
    };

    if (status === "PROCESSING") {
      // Already syncing on Brightcove — ask the user to wait, no publish action.
      return {
        title: t("video_processing_title"),
        subTitle: t("video_processing_warning"),
        closeButtonText: t("ok"),
        closeButtonHandle: () => setShowNonPlayableDialog(false),
        confirmButtonText: "",
        confirmButtonHandle: () => setShowNonPlayableDialog(false),
      };
    }

    if (status === "FAILED") {
      // A previous sync attempt failed — offer a retry or a path to re-publish.
      return {
        title: t("video_failed_title"),
        subTitle: t("video_failed_warning"),
        closeButtonText: t("go_to_asset"),
        closeButtonHandle: goToAssetManager,
        confirmButtonText: syncingVideo ? t("syncing") : t("retry_brightcove_sync"),
        confirmButtonHandle: handleSyncVideoToPlayer,
      };
    }

    // NOT_PLAYABLE (default) — never published, prompt to publish/sync.
    return {
      title: t("video_not_playable"),
      subTitle: t("video_not_playable_popup_subtitle"),
      closeButtonText: t("go_to_listing"),
      closeButtonHandle: goToAssetManager,
      confirmButtonText: syncingVideo ? t("syncing") : t("sync_to_brightcove"),
      confirmButtonHandle: handleSyncVideoToPlayer,
    };
  };

  // Initial fetch for menu content

  useEffect(() => {
    getDamMenucontent();
  }, []);

  // Re-seed the browsed community when the site's DAM mapping changes (site
  // switch) so the gallery does not keep showing the previous tenant's assets.
  useEffect(() => {
    setUuid(rootUuid);
  }, [rootUuid]);
  // Initial fetch for DAM content

  useEffect(() => {
    const nextIndex = 0;
    const filter = {
      author: "",
      tag: [],
    };
    removetag();
    setStartIndex(() => nextIndex);
    getDAMcontent(nextIndex, filter, searchItem, playbackStatusFilter);
    getFacet();
  }, [uuid]);

  useEffect(() => {
    const nextIndex = 0;
    const filter = {
      author: author,
      tag: tag,
    };

    setStartIndex(() => nextIndex);
    getDAMcontent(nextIndex, filter, searchItem, playbackStatusFilter);
  }, [author, sortBy]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const nextIndex = 0;
      const filter = {
        author: author,
        tag: tag,
      };
      setStartIndex(() => nextIndex);
      getDAMcontent(nextIndex, filter, searchItem, playbackStatusFilter);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [tag]);

  const getFilterTitleToShow = (defaultMsg) => {
    let msg = "";
    const assetName = assetType === "Image" ? "Images" : "Videos";
    if (filterVal === "playable") {
      msg = playbackStatusFilter === "ALL" ? defaultMsg : `Showing playable ${assetName} only`;
    } else if (filterVal === "tag" && tag?.length > 0) {
      msg = `Showing ${assetName} for selected "Tags"`;
    } else if (filterVal === "author" && author?.length > 0) {
      msg = `Showing ${assetName} for author "${author}"`;
    } else if (filterVal === "folder" && folder?.name) {
      msg = `Showing ${assetName} in "${folder?.name}" folder`;
    } else {
      msg = defaultMsg;
    }
    return msg;
  };

  // Auto-pagination applies to the top-level folder list only. While drilled
  // into a subfolder the list comes from the `folder` object (not server-paginated),
  // so no further fetching should happen.
  const canLoadMoreFolders = !folder?.name && folderHasMore;

  return (
    <Dialog
      fullScreen
      open={dialogOpen}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'>
      <DialogContent className={classes.dampadding}>
        <Box className='damcontent_container'>
          <DamContentTopHeading /*Component for the top heading, search bar, and action buttons*/
            onSearch={handleSearch}
            inputValue={inputValue}
            setInputValueHandle={setInputValue}
            assetType={assetType}
            toggleGallery={toggleGallery}
            handleDoneClick={handleDoneClick}
            imageData={imageData}
            menuData={menuData}
            toggleDrawer={toggleDrawer}
            loading={processing}
          />
          <Typography variant='h3semibold' className={classes.typographyshow}>
            {assetType === "Image"
              ? getFilterTitleToShow(t("showing_all_images"))
              : getFilterTitleToShow(t("showing_all_videos"))}
          </Typography>
          <Grid container className={classes.parent}>
            <Grid item>
              <Box
                className={classes.filterbox}
                onClick={(e) => setAnchorE2(e.currentTarget)}
                data-testid='set-anchor'>
                <img src={FolderBlackLarge} style={{ filter: "brightness(0)" }} alt='folder' />
                <Typography variant='p3regular' className={classes.typoText}>
                  Search By: {filterVal || "Default"}
                </Typography>
                <img
                  style={{ transform: folderMenuOpen ? "none" : "rotate(270deg)" }}
                  src={ArrowDownBlack}
                  alt='icon'
                />
              </Box>
              <FilterDropdown /*filter dropdown*/
                setFilter={setFilter}
                anchorE2={anchorE2}
                setAnchorE2={setAnchorE2}
                filter={filterVal}
                setSearchQuery={setSearchQuery}
                assetType={assetType}
              />
            </Grid>
            <Grid item>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SortByMenu
                  setSortBy={setSortBy}
                  sortBy={sortBy} /*sort filter menu for new and old images*/
                />
                {assetType === "Video" && (
                  <FilterByMenu
                    filterBy={playbackFilter}
                    setFilterBy={handlePlaybackFilterChange}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
          <Divider />
          <Grid container spacing={0}>
            {folderMenuOpen && (
              <Grid
                container
                className='leftsidebar-scroll'
                item
                xs={12}
                em={3}
                lg={2.5}
                sx={{
                  display: { xs: "none", em: "block" },
                  borderRight: { xs: "none", em: `1px solid ${theme.palette.customColor.LINE}` },
                }}>
                {filterVal !== "default" && (
                  <Grid item>
                    <Box className='damcontentfolder'>
                      <AssetFilter searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                    </Box>
                  </Grid>
                )}

                <DamContentLeftSidebar /* Left sidebar*/
                  toggleDrawer={toggleDrawer}
                  loading={categoryLoading}
                  menuData={menuData}
                  setUuid={setUuid}
                  assetType={assetType}
                  uuid={uuid}
                  searchQuery={searchQuery}
                  setCategoryLoading={setCategoryLoading}
                  folder={folder}
                  setFolder={setFolder}
                  prevFolderState={prevFolderState}
                  setPrevFolderState={setPrevFolderState}
                  totalImageItem={totalImageItem}
                  isImgLoad={loading}
                  folderHasMore={canLoadMoreFolders}
                  fetchMoreFolders={fetchMoreFolders}
                />
              </Grid>
            )}
            {dateMenuOpen && (
              <Grid
                container
                className='leftsidebar-scroll'
                item
                xs={12}
                em={3}
                lg={2.5}
                sx={{
                  display: { xs: "none", em: "block" },
                  borderRight: { xs: "none", em: `1px solid ${theme.palette.customColor.LINE}` },
                }}>
                <Box className={classes.datepickerbox}>
                  <Typography variant='h5semibold'>Start Date</Typography>
                  <Box className={classes.topboxmargin}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        inputFormat='DD/MMM/YYYY'
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            inputProps={{
                              ...params.inputProps,
                              readOnly: true,
                            }}
                          />
                        )}
                        value={scheduleNewDateVal}
                        onChange={(newValue) => {
                          setScheduleNewDateVal(newValue); // Update start date value
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Box>
                <Box className={classes.datepickerbox}>
                  <Typography variant='h5semibold'>End Date</Typography>
                  <Box className={classes.topboxmargin}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        inputFormat='DD/MMM/YYYY'
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            inputProps={{
                              ...params.inputProps,
                              readOnly: true,
                            }}
                          />
                        )}
                        value={scheduleEndDateVal}
                        onChange={(newValue) => {
                          setScheduleEndDateVal(newValue); // Update end date value
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Box>
              </Grid>
            )}
            {tagMenuOpen && (
              <Grid
                container
                className='leftsidebar-scroll'
                item
                xs={12}
                em={3}
                lg={2.5}
                sx={{
                  display: { xs: "none", em: "block" },
                  borderRight: { xs: "none", em: `1px solid ${theme.palette.customColor.LINE}` },
                }}>
                {filterVal !== "default" && (
                  <Grid item>
                    <Box className='damcontentfolder'>
                      <AssetFilter searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                    </Box>
                  </Grid>
                )}
                <TagMenu
                  label={label}
                  tag={tag}
                  setTag={setTag}
                  searchQuery={searchQuery.toLocaleLowerCase()}
                />
              </Grid>
            )}
            {authorMenuOpen && (
              <Grid
                container
                className='leftsidebar-scroll'
                item
                xs={12}
                em={3}
                lg={2.5}
                sx={{
                  display: { xs: "none", em: "block" },
                  borderRight: { xs: "none", em: `1px solid ${theme.palette.customColor.LINE}` },
                }}>
                {filterVal !== "default" && (
                  <Grid item>
                    <Box className='damcontentfolder'>
                      <AssetFilter searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                    </Box>
                  </Grid>
                )}

                <AuthorMenu /* Author Menu for selecting the author from the menubar*/
                  setAuthor={setAuthor}
                  label={label}
                  author={author}
                  searchQuery={searchQuery.toLowerCase()}
                />
              </Grid>
            )}
            <Grid
              item
              xs={12}
              em={filterVal === "default" || filterVal === "playable" ? 12 : 9}
              lg={filterVal === "default" || filterVal === "playable" ? 12 : 9.5}
              className={` right-topbar-container`}>
              <Box className={classesGallery.noresultfoundtypo}>
                {!rootUuid ? (
                  // Say why the picker is empty instead of implying the search
                  // found nothing -- parity with AssetListing's fail-closed state.
                  <Box
                    textAlign='center'
                    sx={{ padding: "40px 10px" }}
                    data-testid='dam-not-configured'>
                    <Typography variant='h5regular'>{t("dam_not_configured")}</Typography>
                  </Box>
                ) : !loading && data?.collectionItem?.length === 0 ? (
                  <NoSearchResult />
                ) : (
                  <Box className={classesGallery.infinitescroll}>
                    {loading ? (
                      <Box
                        sx={{
                          width: "inherit",
                          margin: "16px 0px 0px 16px", //"30px 7.5px 0px 30px"
                        }}>
                        <LoaderComponent />
                      </Box>
                    ) : (
                      <DamContentCard /* getting the images*/
                        data={data}
                        fetchMoreData={fetchMoreData}
                        setImageData={setImageData}
                        rows={ROWS}
                        assetType={assetType}
                        // isSmallScreen={isSmallScreen}
                        // filterVal={filterVal}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
        {showNonPlayableDialog &&
          (() => {
            const dialogConfig = getPlaybackDialogConfig();
            return (
              <CommonPlateformXDialog
                isDialogOpen={showNonPlayableDialog}
                title={dialogConfig.title}
                subTitle={dialogConfig.subTitle}
                closeButtonText={dialogConfig.closeButtonText}
                closeButtonHandle={dialogConfig.closeButtonHandle}
                crossButtonHandle={() => setShowNonPlayableDialog(false)}
                confirmButtonText={dialogConfig.confirmButtonText}
                confirmButtonHandle={dialogConfig.confirmButtonHandle}
                disableConfirmButton={syncingVideo}
                modalType='unsavedChanges'
              />
            );
          })()}
      </DialogContent>
    </Dialog>
  );
};
